import type { IconName } from './icon.constant.js'
import { PERMISSIONS, type PermissionName } from './permission.constant.js'

/**
 * The navigation model: seven rail sections, each with its own items.
 *
 * It is data, not markup: the rail, the section nav and the landing route all render from
 * this one structure. A section whose items are all hidden hides itself — that is what makes
 * the shell permission-driven rather than permission-decorated.
 *
 * `requires` is the same union the routes use, so a typo fails the build. `query` lets two
 * items share one route and differ by filter — "Shop promotions" and "Default promotions"
 * are the same list scoped two ways, and the section nav matches on the query too.
 *
 * `available: false` keeps an unbuilt screen visible and refusing the click with a reason;
 * hiding it is indistinguishable from "you lack the permission". Nothing is unavailable
 * today — the flag stays for the next screen that is announced before it is built.
 */
export interface NavItem {
  readonly label: string
  /**
   * Its own glyph, not the section's.
   *
   * Load-bearing once the section nav is collapsed: the flyout off the rail is then the
   * only way to reach a child screen, and a list of same-looking words is slower to scan
   * than a list of marks. Each is Phosphor's own identity from the closed registry.
   */
  readonly icon: IconName
  readonly route: string | null
  readonly query?: Readonly<Record<string, string>>
  readonly requires?: readonly PermissionName[]
  readonly available: boolean
}

export interface NavSection {
  readonly id: string
  readonly label: string
  readonly icon: IconName
  readonly items: readonly NavItem[]
}

export const NAVIGATION: readonly NavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: 'squares-four',
    // No `requires`: the dashboard itself gates each block on the actor's permissions.
    items: [{ icon: 'gauge', label: 'Dashboard', route: 'overview', available: true }],
  },
  {
    id: 'merchants',
    label: 'Merchants',
    icon: 'storefront',
    /**
     * `Merchants` and `Admin` are the two ACTOR categories, and each carries its own log.
     *
     * Shop activity lives here rather than under Admin because it records what a MERCHANT
     * did; Admin's audit log records what an OPERATOR did. The pairing is exact — in the
     * database they are `shop_events` and `admin_audit_logs` — so an operator who knows
     * where one log lives can find the other by analogy instead of by hunting.
     */
    items: [
      { icon: 'storefront', label: 'Shops', route: 'shops', requires: [PERMISSIONS.shop.view], available: true },
      { icon: 'pulse', label: 'Shop activity', route: 'shop-events', requires: [PERMISSIONS.shopEvent.view], available: true },
    ],
  },
  {
    id: 'promotions',
    label: 'Promotions',
    icon: 'tag',
    items: [
      { icon: 'tag', label: 'Shop promotions', route: 'promotions', query: { scope: 'shop' }, requires: [PERMISSIONS.promotion.view], available: true },
      // `shop_id is null`: the platform templates every new promotion starts from.
      { icon: 'stack', label: 'Default promotions', route: 'promotions', query: { scope: 'template' }, requires: [PERMISSIONS.promotion.view], available: true },
    ],
  },
  {
    id: 'widgets',
    label: 'Widgets',
    icon: 'squares-four',
    items: [
      { icon: 'squares-four', label: 'Shop widgets', route: 'widgets', query: { kind: 'shop' }, requires: [PERMISSIONS.widget.view], available: true },
      // Shared defaults: no shop owns them; every installed shop without an override inherits.
      { icon: 'stack', label: 'Default widgets', route: 'widgets', query: { kind: 'default' }, requires: [PERMISSIONS.widget.view], available: true },
    ],
  },
  {
    id: 'apps',
    label: 'Apps',
    icon: 'seal-check',
    items: [
      { icon: 'puzzle-piece', label: 'Apps', route: 'apps', requires: [PERMISSIONS.app.view], available: true },
      { icon: 'seal-check', label: 'Shopify Functions', route: 'shopify-functions', requires: [PERMISSIONS.shopifyFunction.view], available: true },
    ],
  },
  {
    id: 'revenue',
    label: 'Revenue',
    icon: 'chart-bar',
    items: [
      { icon: 'currency-dollar', label: 'Shop revenue', route: 'revenue', requires: [PERMISSIONS.revenue.view], available: true },
      { icon: 'pulse', label: 'Analytics health', route: 'analytics-health', requires: [PERMISSIONS.analytics.view], available: true },
      { icon: 'receipt', label: 'Plans', route: 'plans', requires: [PERMISSIONS.plan.view], available: true },
      { icon: 'credit-card', label: 'Subscriptions', route: 'subscriptions', requires: [PERMISSIONS.subscription.view], available: true },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'chart-bar',
    items: [
      { icon: 'pulse', label: 'Usage', route: 'reports-usage', requires: [PERMISSIONS.analytics.view], available: true },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: 'shield-check',
    items: [
      { icon: 'users-three', label: 'Operators', route: 'operators', requires: [PERMISSIONS.adminUser.view], available: true },
      { icon: 'user-gear', label: 'Roles', route: 'roles', requires: [PERMISSIONS.role.view], available: true },
      { icon: 'clock-counter-clockwise', label: 'Audit log', route: 'audit-log', requires: [PERMISSIONS.auditLog.view], available: true },
    ],
  },
]
